import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  selectFileIndex, selectMaxSeverity, selectMinSeverity, selectResolveHost, selectWorkspaceSelected
} from 'store/ManageEditCreate/selectors';
import ModalWrapper from 'Common/Components/ModalWrapper';
import { uploadFile, setFileIndex, setUploadState, resetFileUploadState, setFilesCount, setWorkspace, setImportEntity } from 'Screens/Contextualization/ManageEditCreate/actions/Actions';
import { Button, ButtonsContainer } from 'Common/Components/MainContainer/styled';
import { Wrapper, LeftTitle, BackBtn } from './styled';
import DragStep from './components/DragStep';
import ListStep from './components/ListStep';
import { showProcessingQueue } from 'Common/Components/FileUploadContext/actions';
import { buildUploadParams, isAssetImport, isGeneralImport } from './utils';

const ImportFileCreationModal = ({ show, handleClose, entity }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [files, setFiles] = useState([]);
  const [step, setStep] = useState('DRAG');
  const resolveHost = useSelector(selectResolveHost);
  const fileIndex = useSelector(selectFileIndex);
  const workspace = useSelector(selectWorkspaceSelected);
  const minSeverity = useSelector(selectMinSeverity);
  const maxSeverity = useSelector(selectMaxSeverity);

  const titleLabel = intl.formatMessage({
    id: isAssetImport(entity) ? 'importFileCreationModal.assetsTitle' : 'importFileCreationModal.title'
  });
  const doneLabel = intl.formatMessage({ id: 'importFileCreationModal.doneButton' });
  const uploadLabel = intl.formatMessage({ id: 'importFileCreationModal.uploadButton' });
  const cancelLabel = intl.formatMessage({ id: 'importFileCreationModal.cancelButton' });

  const onHandleClose = () => {
    setFiles([]);
    setStep('DRAG');
    dispatch(resetFileUploadState());
    handleClose();
    dispatch(setWorkspace(''));
  };

  const handleDrop = (filesUploading) => {
    const fileList = files;
    for (let i = 0; i < filesUploading.length; i += 1) {
      if (!filesUploading[i].name) return;
      if (fileList.findIndex((item) => (item.name === filesUploading[i].name && item.size === filesUploading[i].size)) === -1) {
        fileList.push(filesUploading[i]);
      }
    }
    setFiles([...fileList]);
    if (fileList.length > 0) setStep('LIST');
    dispatch(setFileIndex(fileList.length - 1));
    dispatch(setUploadState());
  };

  const upload = () => {
    dispatch(setImportEntity(entity, workspace));
    const params = buildUploadParams({
      entity,
      file: files[fileIndex],
      resolveHost,
      minSeverity,
      maxSeverity
    });
    setStep('UPLOAD');
    dispatch(setFilesCount(files));
    if (fileIndex >= 0) dispatch(uploadFile(params, workspace));
    dispatch(showProcessingQueue(true));
    onHandleClose();
  };

  const stepComponent = {
    LIST: <ListStep
      files={ files }
      setFiles={ setFiles }
      setStep={ setStep }
      handleDrop={ handleDrop }
      entity={ entity }
    />,
    DRAG: <DragStep
      files={ files }
      setFiles={ setFiles }
      setStep={ setStep }
      entity={ entity }
    />
  };

  if (!show) return null;
  return (
    <ModalWrapper>
      <Wrapper>
        <LeftTitle children={ titleLabel } />
        { stepComponent[step] }
        <ButtonsContainer>
          { step === 'SUMMARY'
            ? <Button onClick={ onHandleClose } children={ doneLabel } />
            : <Button onClick={ upload } disabled={ step !== 'LIST' || isEmpty(files) || (isGeneralImport(entity) && isEmpty(workspace)) } children={ uploadLabel } />
          }
          <BackBtn onClick={ onHandleClose } children={ cancelLabel } />
        </ButtonsContainer>
      </Wrapper>
    </ModalWrapper>
  );
};

ImportFileCreationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  entity: PropTypes.string
};

ImportFileCreationModal.defaultProps = {
  entity: 'vulns'
};

export default ImportFileCreationModal;
